// ============================================================================
// PeriodFilterDialog.tsx — ЕДИНАЯ (SSoT/SRP) форма ввода ПЕРИОДА для ЛЮБОГО
// списка (все объекты метаданных имеют дату создания). Открывается из
// «Ещё → Период» панели команд списка. Применяется как пользовательский ОТБОР
// по полю created_at (>= «с» и <= «по», конец дня) через тот же механизм
// настроек списка (settingsStore → list.{тип}.{объект}.filters) и тот же
// applyFilters — никаких отдельных путей фильтрации.
// UI: пресеты (сегодня/вчера/неделя/месяц/квартал/год/последние N дней) +
// произвольные даты; нативные input type=date — удобно на десктопе и мобильном.
// ============================================================================

import { useState } from 'react';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { useT } from '@/lib/i18n';

interface PeriodFilterDialogProps {
  open: boolean;
  /** Текущий период из пользовательских отборов ('YYYY-MM-DD' или ''). */
  from: string;
  to: string;
  onApply: (from: string, to: string) => void;
  onReset: () => void;
  onClose: () => void;
}

const pad = (n: number) => String(n).padStart(2, '0');
const isoLocal = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function startOfWeek(d: Date): Date {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // понедельник
  x.setDate(x.getDate() - day);
  return x;
}
function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function startOfQuarter(d: Date): Date {
  const q = Math.floor(d.getMonth() / 3);
  return new Date(d.getFullYear(), q * 3, 1);
}
function startOfYear(d: Date): Date {
  return new Date(d.getFullYear(), 0, 1);
}

function buildPresets(): Array<{ key: string; labelKey: string; from: string; to: string }> {
  const today = new Date();
  const todayIso = isoLocal(today);
  const yesterday = addDays(today, -1);
  const lastMonth = addMonths(today, -1);
  const p = (key: string, labelKey: string, f: Date | string, to: Date | string) => ({
    key, labelKey, from: typeof f === 'string' ? f : isoLocal(f), to: typeof to === 'string' ? to : isoLocal(to),
  });
  return [
    p('today', 'period.today', todayIso, todayIso),
    p('yesterday', 'period.yesterday', yesterday, yesterday),
    p('week7', 'period.last7', addDays(today, -6), todayIso),
    p('days30', 'period.last30', addDays(today, -29), todayIso),
    p('week', 'period.this_week', startOfWeek(today), todayIso),
    p('month', 'period.this_month', startOfMonth(today), todayIso),
    p('prev_month', 'period.prev_month', lastMonth, endOfMonth(lastMonth)),
    p('quarter', 'period.this_quarter', startOfQuarter(today), todayIso),
    p('year', 'period.this_year', startOfYear(today), todayIso),
  ];
}

export function PeriodFilterDialog({ open, from, to, onApply, onReset, onClose }: PeriodFilterDialogProps) {
  const t = useT();
  const [localFrom, setLocalFrom] = useState(from);
  const [localTo, setLocalTo] = useState(to);
  const presets = buildPresets();
  const hasValue = !!(localFrom || localTo);

  const applyPreset = (f: string, t: string) => {
    setLocalFrom(f);
    setLocalTo(t);
  };

  return (
    <UniversalWindow
      open={open}
      onClose={onClose}
      title={t('period.title')}
      resizable={false}
      width="w-[min(480px,94vw)]"
    >
      <div className="p-3 space-y-3 text-xs">
        {/* Быстрые пресеты — крупные кнопки (удобно на тач-экранах) */}
        <div className="space-y-1">
          <div className="text-[10px] text-muted-foreground">{t('period.quick')}</div>
          <div className="flex flex-wrap gap-1.5">
            {presets.map((pr) => {
              const active = localFrom === pr.from && localTo === pr.to;
              return (
                <button
                  key={pr.key}
                  type="button"
                  onClick={() => applyPreset(pr.from, pr.to)}
                  className={`text-[11px] rounded-full px-2.5 py-1 border transition-colors ${active ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted'}`}
                >
                  {t(pr.labelKey)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Произвольный период */}
        <div className="grid grid-cols-2 gap-2">
          <label className="space-y-1">
            <span className="block text-[10px] text-muted-foreground">{t('period.from')}</span>
            <input
              type="date"
              value={localFrom}
              onChange={(e) => setLocalFrom(e.target.value)}
              className="w-full rounded-md border bg-input px-2 py-1.5 text-xs"
            />
          </label>
          <label className="space-y-1">
            <span className="block text-[10px] text-muted-foreground">{t('period.to_inclusive')}</span>
            <input
              type="date"
              value={localTo}
              onChange={(e) => setLocalTo(e.target.value)}
              className="w-full rounded-md border bg-input px-2 py-1.5 text-xs"
            />
          </label>
        </div>
        <div className="text-[10px] text-muted-foreground leading-snug">
          {t('period.hint')}
        </div>

        <div className="flex items-center gap-2 border-t pt-2">
          {hasValue && (
            <button
              type="button"
              className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
              onClick={() => { setLocalFrom(''); setLocalTo(''); }}
            >
              {t('period.clear')}
            </button>
          )}
          {(from || to) && (
            <button
              type="button"
              className="text-[11px] text-destructive hover:underline underline-offset-2"
              onClick={() => { onReset(); onClose(); }}
              title={t('period.reset_hint')}
            >
              {t('period.reset')}
            </button>
          )}
          <div className="flex-1" />
          <button type="button" className="rounded-md border px-3 py-1.5 text-xs hover:bg-muted" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-xs hover:opacity-90 disabled:opacity-50"
            disabled={!hasValue}
            onClick={() => { onApply(localFrom, localTo); onClose(); }}
          >
            {t('common.apply')}
          </button>
        </div>
      </div>
    </UniversalWindow>
  );
}
